import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, TextField, Button, Box, Card, CardContent, Stepper, Step, StepLabel, CircularProgress } from '@mui/material';
import { Search, LocalShipping } from '@mui/icons-material';
import api from '../../services/api';

const TrackShipmentPage = () => {
  const { number } = useParams();
  const [trackingNumber, setTrackingNumber] = useState(number || '');
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const statusSteps = [
    'pending', 'picked_up', 'at_origin_warehouse', 'in_transit_international',
    'customs_import', 'cleared', 'out_for_delivery', 'delivered'
  ];

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;
    setLoading(true); setError('');
    try {
      const { data } = await api.get(`/shipments/track/${trackingNumber}`);
      setShipment(data.data);
    } catch (err) {
      setError('Shipment not found. Please check the tracking number.');
      setShipment(null);
    }
    setLoading(false);
  };

  const getCurrentStep = () => {
    if (!shipment) return 0;
    const idx = statusSteps.indexOf(shipment.status);
    return idx >= 0 ? idx : 0;
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
        <LocalShipping sx={{ fontSize: 40, verticalAlign: 'middle', mr: 1 }} />
        Track Your Shipment
      </Typography>

      <Box component="form" onSubmit={handleTrack} sx={{ display: 'flex', gap: 2, my: 4 }}>
        <TextField fullWidth placeholder="Enter shipment number (e.g., SHP-202601-000001)"
          value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
        <Button type="submit" variant="contained" startIcon={<Search />} sx={{ px: 4 }}>Track</Button>
      </Box>

      {loading && <Box textAlign="center" py={4}><CircularProgress /></Box>}
      {error && <Typography color="error" textAlign="center">{error}</Typography>}

      {shipment && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Shipment: {shipment.shipmentNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Method: {shipment.shippingMethod?.toUpperCase()} | Carrier: {shipment.carrier?.name || 'N/A'}
            </Typography>

            <Box sx={{ my: 4 }}>
              <Stepper activeStep={getCurrentStep()} alternativeLabel>
                {statusSteps.map((step) => (
                  <Step key={step}>
                    <StepLabel>{step.replace(/_/g, ' ').toUpperCase()}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {shipment.statusHistory?.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Status History</Typography>
                {shipment.statusHistory.map((entry, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2, py: 1, borderBottom: '1px solid #eee' }}>
                    <Typography variant="caption" sx={{ minWidth: 140 }}>
                      {new Date(entry.timestamp).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {entry.status?.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    {entry.location && <Typography variant="body2" color="text.secondary">{entry.location}</Typography>}
                  </Box>
                ))}
              </Box>
            )}

            {shipment.estimatedDelivery && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Estimated Delivery: <strong>{new Date(shipment.estimatedDelivery).toLocaleDateString()}</strong>
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default TrackShipmentPage;
